import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { useLiveStatus } from '../../contexts/LiveStatusContext'
import { formatOrderNr } from '../../lib/orderNr'
import {
  markOrderPrinted,
  printedOrderIds,
  useDevicePrinter,
} from '../../lib/receiptPrinter/deviceSettings'
import {
  loadOrderForSlip,
  loadWorkSlipSettings,
  pickOrdersToAutoPrint,
  printReceipt,
  type AutoPrintCandidate,
} from '../../lib/receiptPrinter/printOrder'
import type { WorkSlipSettings } from '../../lib/orderWorkSlip'

/**
 * Auto-print: on the shop tablet, each newly placed order prints by itself.
 *
 * Mounted once by Shell; does nothing unless this device turned auto-print on
 * (Board → Printer). It keeps a small list of the newest orders. The live order
 * stream (ADR-W12) invalidates every ['orders', …] query on each order event, so
 * this list refreshes the moment an order changes; without the stream it polls.
 * Each order prints once per device: printed ids are kept in this browser.
 */

export const AUTO_PRINT_LIST_SIZE = 20
const POLL_MS_OFFLINE = 15_000
const POLL_MS_LIVE = 60_000
const MAX_ATTEMPTS = 3
const SETTINGS_TTL_MS = 5 * 60_000

export interface AutoPrintStatus {
  lastPrinted: { orderNr: string; at: string } | null
  lastError: string | null
}

let status: AutoPrintStatus = { lastPrinted: null, lastError: null }
const statusListeners = new Set<() => void>()
function setStatus(patch: Partial<AutoPrintStatus>) {
  status = { ...status, ...patch }
  statusListeners.forEach((l) => l())
}

export function useAutoPrintStatus(): AutoPrintStatus {
  return useSyncExternalStore(
    (l) => {
      statusListeners.add(l)
      return () => statusListeners.delete(l)
    },
    () => status,
    () => status,
  )
}

/** Keeps the screen on while auto-print is on, where the browser allows it. */
function useWakeLock(enabled: boolean) {
  useEffect(() => {
    if (!enabled || typeof navigator === 'undefined' || !('wakeLock' in navigator)) return
    let lock: { release: () => Promise<void> } | null = null
    let cancelled = false
    const request = async () => {
      try {
        const next = await (navigator as Navigator & {
          wakeLock: { request: (type: 'screen') => Promise<{ release: () => Promise<void> }> }
        }).wakeLock.request('screen')
        if (cancelled) void next.release()
        else lock = next
      } catch {
        // Not allowed right now (battery saver, hidden tab): try again when visible.
      }
    }
    const onVisible = () => {
      if (!document.hidden) void request()
    }
    void request()
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisible)
      void lock?.release().catch(() => {})
    }
  }, [enabled])
}

export default function AutoPrintWatcher() {
  const device = useDevicePrinter()
  const { connected } = useLiveStatus()
  const enabled = device.method === 'rawbt' && device.autoPrint && Boolean(device.autoPrintSince)
  useWakeLock(enabled)

  const { data } = useQuery({
    // Under ['orders'] so the live stream's invalidations refresh it.
    queryKey: ['orders', 'autoprint'],
    queryFn: async () => {
      const res = await api.get<{ content?: AutoPrintCandidate[] }>('/orders', {
        params: { size: AUTO_PRINT_LIST_SIZE, page: 0, sort: 'createdAt,desc', hideCancelled: true },
      })
      return res.data?.content ?? []
    },
    enabled,
    refetchInterval: connected ? POLL_MS_LIVE : POLL_MS_OFFLINE,
    refetchIntervalInBackground: true,
  })

  const busy = useRef(false)
  const attempts = useRef(new Map<string, number>())
  const settingsCache = useRef<{ value: WorkSlipSettings; at: number } | null>(null)
  const deviceRef = useRef(device)
  // Bumped after a run, so orders that arrived meanwhile are looked at at once.
  const [round, setRound] = useState(0)
  deviceRef.current = device

  useEffect(() => {
    if (!enabled || !data || busy.current) return
    const queue = pickOrdersToAutoPrint(data, device, new Set(printedOrderIds())).filter(
      (o) => (attempts.current.get(o.id) ?? 0) < MAX_ATTEMPTS,
    )
    if (queue.length === 0) return

    busy.current = true
    void (async () => {
      try {
        for (const candidate of queue) {
          // Turned off, or switched away from RawBT, while printing.
          const now = deviceRef.current
          if (!now.autoPrint || now.method !== 'rawbt') break
          attempts.current.set(candidate.id, (attempts.current.get(candidate.id) ?? 0) + 1)
          try {
            if (!settingsCache.current || Date.now() - settingsCache.current.at > SETTINGS_TTL_MS) {
              settingsCache.current = { value: await loadWorkSlipSettings(), at: Date.now() }
            }
            const order = await loadOrderForSlip(candidate.id)
            await printReceipt(order, settingsCache.current.value, 'rawbt')
            markOrderPrinted(candidate.id)
            setStatus({
              lastPrinted: { orderNr: order.orderNr || formatOrderNr(order.id), at: new Date().toISOString() },
              lastError: null,
            })
          } catch (err) {
            setStatus({ lastError: err instanceof Error ? err.message : 'Auto-print failed' })
          }
        }
      } finally {
        busy.current = false
        setRound((r) => r + 1)
      }
    })()
  }, [data, enabled, device, round])

  return null
}
